// ============================================================================
// Review-queue Resolve dialog (Cowork 02, 2026-06-28).
//
// For a new_account_sop card stuck at materialize_status = 'needs_review'.
// Since 2026-09-10 the BUILDING already exists — the card minted it on its
// first save — and what is missing is the CUSTOMER. Leadership (superuser /
// executive / national AM) settles it one of two ways via sop_resolve_review:
//
//   create_account  → mint a fresh customer and put the existing building
//                     under it.
//   link_existing   → put the existing building under a customer we already
//                     have (requires the target account_id, e.g. ACT-0123).
//
// Either way no second facility appears. Cards that predate the change have
// no building of their own; the RPC still creates one for those.
//
// The RPC is gated server-side to has_full_am_authority(); a non-leadership
// caller gets a 42501 whose .message is surfaced here (same error-surfacing
// pattern as resolution-dialog.tsx).
// ============================================================================

import { useState } from 'react';
import { GitMerge, PlusCircle, Link2 } from 'lucide-react';
import { Button, useToast } from '../../shared/components';
import { resolveReviewCard } from './hooks/use-needs-info';
import { errorMessage } from '../../shared/errors';

export interface ReviewResolveTarget {
  sopId: string;
  displayName: string;
  /** The building this card already made, if it has one. */
  facilityId?: string | null;
}

export function ReviewResolveDialog({
  target,
  onClose,
  onResolved,
}: {
  target: ReviewResolveTarget;
  onClose: () => void;
  onResolved: () => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<'choose' | 'link'>('choose');
  const [accountId, setAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<void>, successMsg: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      toast.success(successMsg);
      onResolved();
      onClose();
    } catch (e) {
      // Supabase RPC errors are PostgrestError objects, not Error instances.
      // errorMessage() is the shared unwrapper (AFB-0054) — it also translates
      // constraint violations, which the hand-rolled version here did not.
      setError(errorMessage(e, 'Something went wrong.', 'sop-review-resolve'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-tcs-scrim/60" />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl bg-tcs-bg p-5 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-st-warn-bg-strong text-st-warn-fg">
            <GitMerge className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wide text-st-warn-fg">
              Needs a customer
            </div>
            <h2 className="text-base font-semibold text-tcs-text">{target.displayName}</h2>
            <p className="mt-1 text-sm text-tcs-text-secondary">
              {target.facilityId ? (
                <>
                  <span className="font-mono text-tcs-text">{target.facilityId}</span> already
                  exists — nobody has said whose building it is. Create the customer, or put it
                  under one you already have.
                </>
              ) : (
                <>
                  This older card has no building yet. Create a new account for it, or link it to
                  an existing account so its onboarding can complete.
                </>
              )}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-rag-red">
            {error}
          </div>
        )}

        {mode === 'choose' && (
          <div className="mt-4 space-y-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => resolveReviewCard('create_account', target.sopId), 'Customer created')}
              className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted disabled:opacity-50"
            >
              <PlusCircle className="h-5 w-5 shrink-0 text-rag-green" />
              <span>
                <span className="block text-sm font-medium text-tcs-text">Create new customer</span>
                <span className="block text-xs text-tcs-text-secondary">
                  {target.facilityId
                    ? 'Create a customer from this card and put the building under it.'
                    : 'Create a fresh account + facility from this card.'}
                </span>
              </span>
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode('link')}
              className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted disabled:opacity-50"
            >
              <Link2 className="h-5 w-5 shrink-0 text-tcs-accent-ink" />
              <span>
                <span className="block text-sm font-medium text-tcs-text">Link to existing customer</span>
                <span className="block text-xs text-tcs-text-secondary">
                  Put the building under a customer you already have.
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full pt-1 text-center text-xs text-tcs-text-muted hover:text-tcs-text-secondary"
            >
              I'll handle this later
            </button>
          </div>
        )}

        {mode === 'link' && (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-tcs-text-secondary">
                Existing customer ID (e.g. ACT-0123)
              </span>
              <input
                type="text"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value.trim())}
                placeholder="ACT-…"
                className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text focus:outline-none focus:ring-2 focus:ring-tcs-accent"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMode('choose')} disabled={busy}>
                Back
              </Button>
              <Button
                size="sm"
                disabled={busy || accountId.length === 0}
                onClick={() =>
                  run(
                    () => resolveReviewCard('link_existing', target.sopId, accountId),
                    'Building linked to its customer',
                  )
                }
              >
                Link
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
